import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { purchasePaymentsApi } from '../api/purchasePayments';
import type { PaymentMode } from '@coreledger/shared-types';

// Vendor-side mirror of RecordPaymentModal.tsx — kept as its own small
// component rather than a generic/prop-injected version of that one, since
// the two differ in their mutation target (purchasePaymentsApi vs
// paymentsApi) and query-invalidation keys, and the form itself is short
// enough (~40 lines) that duplicating it is clearer than abstracting over
// both API clients' shapes.
const PAYMENT_MODES: PaymentMode[] = ['cash', 'upi', 'bank_transfer', 'cheque', 'other'];
const today = () => new Date().toISOString().slice(0, 10);
const fmt0 = (n: number) => '₹' + (n ?? 0).toLocaleString('en-IN', { maximumFractionDigits: 0 });

interface Props {
  purchaseInvoiceId: string;
  invoiceNumber?: string;
  balanceDue: number;
  onClose: () => void;
  onSuccess?: () => void;
}

export default function RecordPurchasePaymentModal({ purchaseInvoiceId, invoiceNumber, balanceDue, onClose, onSuccess }: Props) {
  const qc = useQueryClient();
  const [form, setForm] = useState({ amount: balanceDue > 0 ? balanceDue : undefined as number | undefined, paymentDate: today(), mode: 'bank_transfer' as PaymentMode, reference: '' });
  const [error, setError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: () => purchasePaymentsApi.create({ ...form, purchaseInvoiceId }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['purchase-payments', purchaseInvoiceId] });
      qc.invalidateQueries({ queryKey: ['purchases'] });
      qc.refetchQueries({ type: 'all' });
      onSuccess?.();
      onClose();
    },
    onError: err => setError(err instanceof Error ? err.message : 'Failed to record payment'),
  });

  function handleSave() {
    if (!form.amount || form.amount <= 0) { setError('Enter a valid amount'); return; }
    if (!form.paymentDate) { setError('Payment date is required'); return; }
    setError(null);
    save.mutate();
  }

  return (
    <div className="modal-overlay" onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="modal" style={{ maxWidth: 460 }}>
        <div className="modal-header">
          <span className="modal-title">Record Payment{invoiceNumber ? ` — ${invoiceNumber}` : ''}</span>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>

        <div className="modal-body">
          {balanceDue > 0 && (
            <div style={{ marginBottom: '1rem', padding: '0.6rem 0.85rem', borderRadius: 6, background: 'var(--primary-light)', fontSize: '0.875rem' }}>
              Balance due: <strong>{fmt0(balanceDue)}</strong>
            </div>
          )}
          {error && <div className="error-box" style={{ marginBottom: '0.75rem' }}>{error}</div>}

          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            <label style={{ fontWeight: 500, fontSize: '0.875rem' }}>
              Amount *
              <input
                type="number" min={0} step={0.01}
                value={form.amount ?? ''}
                onChange={e => setForm(f => ({ ...f, amount: parseFloat(e.target.value) || 0 }))}
                placeholder={fmt0(balanceDue)}
                autoFocus
                style={{ marginTop: '0.3rem' }}
              />
            </label>
            <label style={{ fontWeight: 500, fontSize: '0.875rem' }}>
              Date *
              <input type="date" value={form.paymentDate} onChange={e => setForm(f => ({ ...f, paymentDate: e.target.value }))} style={{ marginTop: '0.3rem' }} />
            </label>
            <label style={{ fontWeight: 500, fontSize: '0.875rem' }}>
              Mode
              <select value={form.mode} onChange={e => setForm(f => ({ ...f, mode: e.target.value as PaymentMode }))} style={{ marginTop: '0.3rem', width: '100%' }}>
                {PAYMENT_MODES.map(m => <option key={m} value={m} style={{ textTransform: 'capitalize' }}>{m.replace('_', ' ')}</option>)}
              </select>
            </label>
            <label style={{ fontWeight: 500, fontSize: '0.875rem' }}>
              Reference
              <input value={form.reference} onChange={e => setForm(f => ({ ...f, reference: e.target.value }))} placeholder="Cheque / UTR / TXN #" style={{ marginTop: '0.3rem' }} />
            </label>
          </div>
        </div>

        <div className="modal-footer">
          <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={save.isPending}>
            {save.isPending ? <span className="spinner" style={{ width: 14, height: 14 }} /> : 'Save Payment'}
          </button>
        </div>
      </div>
    </div>
  );
}
